import { ArrowRight } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';

import CModal from '../../../components/CModal';
import CPageModule from '../../../components/CPageModule';
import CStatefulContainer from '../../../components/CStatefulContainer';
import CTextFieldDisplay from '../../../components/CTextFieldDisplay';
import TagButton from '../../../components/TagButton';
import XDEmptyResult from '../../../components/alerts/XDEmptyResult';
import XDResponsiveGridWrapper from '../../../components/layout/XDResponsiveGridWrapper';
import { ETonalButtonClassNames } from '../../../enums/ETagButtonColors';
import HGeneric from '../../../helpers/HGeneric';
import { updateStateAttribute } from '../../../helpers/helper';
import type { IResponse } from '../../../models/IResponse';
import { type TTradeOrdersHistory } from '../../../models/TTradeOrdersHistory';
import { SvTradeOrderServices } from '../../../services/SvTradeOrderServices';

interface Props {
  onClose: () => void;
  lineageKey?: string;
  accountIDLabel: string;
  ignoreOverride: boolean;
  securityID: string;
  accountID: string;
  securityName: string;
}

interface State {
  orderHistory?: IResponse<TTradeOrdersHistory>;
}

export default function TradeOrdersHistoryModal(props: Props) {
  const [state, setState] = useState<State>({ orderHistory: { state: { isLoading: false, isError: false } } });

  const getHistory = async (lineageKey: string) => {
    updateStateAttribute<State>(
      {
        orderHistory: {
          ...state.orderHistory,
          state: { isError: false, ...state.orderHistory?.state, isLoading: true }
        }
      },
      setState
    );
    const response = await SvTradeOrderServices.getHistory(lineageKey);
    updateStateAttribute<State>(
      {
        orderHistory: {
          ...state.orderHistory,
          data: await response.getData(),
          state: {
            isError: !(await response.isSuccess()),
            isLoading: false,
            strError: !(await response.isSuccess()) ? await response.getMessage() : undefined
          }
        }
      },
      setState
    );
  };

  const handleClose = () => {
    updateStateAttribute<State>({ orderHistory: undefined }, setState);
    props.onClose();
  };

  const sortedHistory = state.orderHistory?.data?.sort((a, b) => {
    return new Date(b.created_ts).getTime() - new Date(a.created_ts).getTime();
  });

  useEffect(() => {
    (async () => {
      if (props.lineageKey) {
        await getHistory(props.lineageKey);
      }
    })();
  }, [props.lineageKey]);

  return (
    <CModal onClose={handleClose} open={!!props.lineageKey}>
      <CPageModule submodule style={{ paddingLeft: 0, paddingRight: 0 }}>
        <div>
          {props.securityName !== props.securityID && <p className={'opacity-80'}>{props.securityID}</p>}
          <h3>{props.securityName}</h3>
        </div>
        <CTextFieldDisplay label={props.accountIDLabel}>{props.accountID}</CTextFieldDisplay>

        <CStatefulContainer apiRequestState={state.orderHistory?.state} showContent={!!state.orderHistory?.data}>
          <div className={'rounded-lg overflow-hidden border'}>
            <div className={'p-4 bg-gray-50'}>
              <XDResponsiveGridWrapper type={'list'}>
                {sortedHistory?.length === 0 && <XDEmptyResult />}
                {sortedHistory?.map((entry, ix) => (
                  <div
                    key={`${entry.created_ts}-${ix}`}
                    className={'p-4 rounded-xl flex flex-col gap-2 bg-white'}
                    style={{
                      opacity: props.ignoreOverride && !entry.system ? 0.5 : 1
                    }}
                  >
                    <CPageModule submodule fullWidth>
                      <div
                        className={
                          'uppercase font-bold text-sm flex flex-wrap gap-2 gap-y-1 items-center justify-between'
                        }
                      >
                        <p className={'opacity-80'}>
                          {HGeneric.formatString(entry.created_ts, { in: 'tzTimestamp', out: 'localeTimeString' })}
                        </p>
                        <TagButton
                          color={entry.system ? ETonalButtonClassNames.TEAL_COMP_1 : ETonalButtonClassNames.TEAL_COMP_4}
                        >
                          {entry.initiator}
                        </TagButton>
                      </div>
                      <XDResponsiveGridWrapper type={'doubleList'}>
                        {entry.diffs.map((diff, diffIX) => (
                          <CTextFieldDisplay label={diff.key} key={diffIX}>
                            <p>
                              {diff.was && (
                                <span className={'opacity-50'}>
                                  {diff.was}
                                  <ArrowRight className={'-mt-0.5 text-[90%]'} />
                                </span>
                              )}
                              {diff.value}
                            </p>
                          </CTextFieldDisplay>
                        ))}
                      </XDResponsiveGridWrapper>
                    </CPageModule>
                  </div>
                ))}
              </XDResponsiveGridWrapper>
            </div>
          </div>
        </CStatefulContainer>
      </CPageModule>
    </CModal>
  );
}
